import React, { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Loader2, Copy, RotateCcw } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { authApi } from "../../lib/auth";
import { errorMessage } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

function RecoveryCodes({ codes }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      toast.success("Recovery codes copied");
    } catch {
      toast("Copy failed - write the codes down");
    }
  };
  return (
    <div className="recovery" data-testid="recovery-codes">
      <p className="muted">Store these somewhere safe. Each code signs you in once if you lose your authenticator.</p>
      <ul className="mono">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <button type="button" className="tb-btn sm" onClick={copy}>
        <Copy size={13} /> Copy codes
      </button>
    </div>
  );
}

function EnrollDialog({ open, onOpenChange, onDone }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (open && !setup) authApi.mfaSetup().then(setSetup).catch((err) => toast.error("Could not start MFA setup", { description: errorMessage(err) }));
  }, [open, setup]);

  const enable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await authApi.mfaEnable(code);
      setCodes(res.recovery_codes);
      onDone();
      toast.success("MFA enabled");
    } catch (err) {
      toast.error("Could not enable MFA", { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="mfa-enroll-dialog">
        {codes ? (
          <>
            <DialogHeader>
              <DialogTitle>Save your recovery codes</DialogTitle>
              <DialogDescription>MFA is now on for your account.</DialogDescription>
            </DialogHeader>
            <RecoveryCodes codes={codes} />
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)} data-testid="mfa-enroll-done">
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={enable}>
            <DialogHeader>
              <DialogTitle>Set up authenticator app</DialogTitle>
              <DialogDescription>Scan the QR code with Microsoft Authenticator, Google Authenticator, 1Password or any TOTP app, then enter the 6-digit code.</DialogDescription>
            </DialogHeader>
            {setup ? (
              <div className="mfa-setup">
                <img src={setup.qr_png} alt="MFA QR code" width={180} height={180} data-testid="mfa-qr" />
                <div className="mfa-secret">
                  <span className="muted">Can't scan? Enter this key manually:</span>
                  <code className="mono" data-testid="mfa-secret">
                    {setup.secret}
                  </code>
                </div>
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" inputMode="numeric" autoComplete="one-time-code" data-testid="mfa-enroll-code" />
              </div>
            ) : (
              <div className="ws-empty">
                <Loader2 size={18} className="spin" />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy || code.replace(/\s/g, "").length < 6} data-testid="mfa-enroll-submit">
                {busy && <Loader2 size={14} className="spin" />} Turn on MFA
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function MfaCard() {
  const { user, org, refresh } = useAuth();
  const [enroll, setEnroll] = useState(false);
  const [disable, setDisable] = useState({ password: "", code: "" });
  const [showDisable, setShowDisable] = useState(false);
  const [regenCode, setRegenCode] = useState("");
  const [newCodes, setNewCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  const doDisable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await authApi.mfaDisable(disable);
      await refresh();
      setShowDisable(false);
      toast.success("MFA disabled");
    } catch (err) {
      toast.error("Could not disable MFA", { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const regen = async () => {
    setBusy(true);
    try {
      const res = await authApi.mfaRecoveryCodes(regenCode);
      setNewCodes(res.recovery_codes);
      setRegenCode("");
      refresh();
    } catch (err) {
      toast.error("Could not regenerate codes", { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ws-card glass" data-testid="mfa-card">
      <h2>
        {user.mfa_enabled ? <ShieldCheck size={16} className="ok" /> : <ShieldOff size={16} className="warn" />} Two-step verification (MFA)
      </h2>
      {user.mfa_enabled ? (
        <>
          <p className="muted">
            MFA is <b className="ok">on</b>. {user.recovery_codes_left} recovery codes left.
            {org?.require_mfa && " Your organization requires MFA."}
          </p>
          <div className="ws-inline-form">
            <Input value={regenCode} onChange={(e) => setRegenCode(e.target.value)} placeholder="Authenticator code" className="w-44" data-testid="mfa-regen-code" />
            <button type="button" className="tb-btn sm" disabled={busy || regenCode.length < 6} onClick={regen} data-testid="mfa-regen">
              <RotateCcw size={13} /> New recovery codes
            </button>
            {!org?.require_mfa && (
              <button type="button" className="tb-btn sm ghost danger" onClick={() => setShowDisable((s) => !s)} data-testid="mfa-disable-toggle">
                Turn off MFA
              </button>
            )}
          </div>
          {newCodes && <RecoveryCodes codes={newCodes} />}
          {showDisable && (
            <form className="ws-inline-form" onSubmit={doDisable} data-testid="mfa-disable-form">
              <Input type="password" value={disable.password} onChange={(e) => setDisable((d) => ({ ...d, password: e.target.value }))} placeholder="Your password" required className="w-48" data-testid="mfa-disable-password" />
              <Input value={disable.code} onChange={(e) => setDisable((d) => ({ ...d, code: e.target.value }))} placeholder="Authenticator code" required className="w-40" data-testid="mfa-disable-code" />
              <button type="submit" className="tb-btn sm danger" disabled={busy} data-testid="mfa-disable-submit">
                Confirm
              </button>
            </form>
          )}
        </>
      ) : (
        <>
          <p className="muted">Protect your account with a 6-digit code from an authenticator app. {org?.require_mfa && <b className="warn">Your organization requires MFA - please enable it now.</b>}</p>
          <button type="button" className="tb-btn primary" onClick={() => setEnroll(true)} data-testid="mfa-enable-btn">
            <ShieldCheck size={15} /> Set up MFA
          </button>
        </>
      )}
      {enroll && <EnrollDialog open onOpenChange={setEnroll} onDone={refresh} />}
    </section>
  );
}
